/** WALL ANGLES AS AN ARCHITECT READS AND SQUARES THEM (OB-48).
 *
 *  Kept out of `model.ts` on purpose: the assistant reads every export of
 *  `model.ts` as a gesture it can call and sends the whole list in its first
 *  message (`asistent/api.ts`, capped at 20 000 characters). These are the
 *  panel's helpers, not gestures. */
import type { Pt, TronsonDoc } from "../doc";
import { axaPeretelui } from "./model";
/** The importer's windows (`finalizare.DIAGONALA_GRADE`, `ALINIERE_GRADE`):
 *  how far off a diagonal or a right angle a wall is still taken as meant to
 *  be one. */
const FEREASTRA_DIAGONALA = 5;
const FEREASTRA_DREAPTA = 10;

/** The angle a wall at `u` (degrees, any convention) is squared to inside the
 *  importer's windows, or `null` beyond both. */
export function unghiulPatrat(u: number): number | null {
  const d = 45 + 90 * Math.round((u - 45) / 90);
  if (Math.abs(u - d) <= FEREASTRA_DIAGONALA) return d;
  const c = 90 * Math.round(u / 90);
  if (Math.abs(u - c) <= FEREASTRA_DREAPTA) return c;
  return null;
}

/** Where «Îndreaptă peretele» turns a wall at `u`: the nearest multiple of
 *  45°, since the editor draws on 0°, 45° and 90° alike. */
export function unghiulIndreptat(u: number): number {
  return 45 * Math.round(u / 45);
}

/** A wall's direction as an architect reads it: counter-clockwise from
 *  horizontal, 0°–180°. The plan's y axis points down, so the screen's
 *  clockwise `atan2` angle `u` is turned round, and a wall has no direction,
 *  so a reading and its opposite are one. */
export function unghiulDeCitit(u: number): number {
  const r = Math.round((((-u % 180) + 180) % 180) * 100) / 100;
  return r === 180 ? 0 : r;
}

/** The plan direction for a typed reading `g`: the wall's axis direction `u`
 *  turned by the difference in readings, folded into (−90°, 90°], so typing
 *  turns the wall instead of swapping its ends — openings are counted from
 *  end a. A perpendicular reading is the tie, and it turns +90° in reading. */
export function unghiulDinCitit(g: number, u: number): number {
  let d = ((g - unghiulDeCitit(u)) % 180 + 180) % 180;
  if (d > 90) d -= 180;
  return (((u - d) % 360) + 360) % 360;
}

/** The walls a few degrees off a diagonal or a right angle — those the
 *  importer would have squared — by index. 0.05°, not less: squaring puts the
 *  ends on whole millimetres, which leaves up to 0.04° on a short wall. */
export function peretiStrambi(doc: TronsonDoc): number[] {
  const out: number[] = [];
  (doc.plan?.walls ?? []).forEach((w, i) => {
    if (w.bulge) return;
    const [a, b] = axaPeretelui(w);
    const u = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
    const to = unghiulPatrat(u);
    if (to !== null && Math.abs(to - u) > 0.05) out.push(i);
  });
  return out;
}

/** A wall outline's length along the wall: the extent of its points along its
 *  longest edge. On a straight wall this is the bounding box's long side, as
 *  `model::osiaPeretelui` gives it; on a diagonal the bounding box is not the
 *  wall's length (K3 of the 45° corner read 4,47 for 5,98 m). */
export function lungimeaInelului(inel: readonly (readonly number[])[]): number {
  let d: Pt = [1, 0], best = -1;
  for (let k = 0; k < inel.length; k++) {
    const a = inel[k], b = inel[(k + 1) % inel.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L > best) { best = L; d = [(b[0] - a[0]) / (L || 1), (b[1] - a[1]) / (L || 1)]; }
  }
  const t = inel.map((q) => q[0] * d[0] + q[1] * d[1]);
  return Math.max(...t) - Math.min(...t);
}
